import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToWebPush } from '../utils/webPush';
import { useBackendHealth } from '../hooks/useBackendHealth';
import { socketIoOptions, socketBaseUrl } from '../utils/socketBaseUrl';

type NotificationPayload = {
  title?: string;
  body?: string;
  notificationId?: string;
  data?: Record<string, unknown>;
};

/**
 * Subscribes to admin in-app notifications via Socket.IO and Web Push (web-push npm on server).
 */
export function useAdminNotifications(onNotification?: (payload: NotificationPayload) => void) {
  const socketRef = useRef<Socket | null>(null);
  const handlerRef = useRef(onNotification);
  handlerRef.current = onNotification;
  const queryClient = useQueryClient();
  const { isBackendUp } = useBackendHealth();

  const connect = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token || !isBackendUp || socketRef.current?.connected) return;

    const base = socketBaseUrl();
    const socket = io(base, {
      auth: { token },
      ...socketIoOptions(),
    });

    socket.on('notifications', (payload: NotificationPayload) => {
      handlerRef.current?.(payload);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    });

    // Vendor open/close — refresh Admin Open chips without waiting for staleTime.
    socket.on('orders:update', (payload: { type?: string } | null) => {
      if (payload?.type !== 'merchant_store_status') return;
      queryClient.invalidateQueries({ queryKey: ['restaurants-admin'] });
      queryClient.invalidateQueries({ queryKey: ['grocery-stores-admin'] });
    });

    socketRef.current = socket;
  }, [queryClient, isBackendUp]);

  useEffect(() => {
    if (!isBackendUp) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }
    connect();
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [connect, isBackendUp]);

  const requestBrowserPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported' as const;
    if (Notification.permission === 'granted') return 'granted' as const;
    if (Notification.permission === 'denied') return 'denied' as const;
    const result = await Notification.requestPermission();
    return result;
  }, []);

  /** Silent re-subscribe when permission already granted (safe after login). */
  const registerWebPushToken = useCallback(async (interactive = false) => {
    try {
      const api = (await import('../services/api')).default;
      return subscribeToWebPush(
        async (subscription) => {
          await api.post('/notifications/web-push/subscribe', subscription);
        },
        { interactive },
      );
    } catch {
      return false;
    }
  }, []);

  return { requestBrowserPermission, registerWebPushToken };
}
